/**
 * État 104 Generator Component
 * 
 * UI for generating État 104 (annual client statement)
 * Allows year selection, preview, and export to Excel/PDF
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  FileSpreadsheet, 
  FileText, 
  Download, 
  Calendar,
  CheckCircle,
  AlertCircle,
  Loader2,
  Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/toast/useToast';
import { Etat104Generator } from '@/lib/services/etat104Generator';
import { supabase } from '@/lib/supabase';
import type { Etat104Summary } from '@/lib/services/etat104Generator';

export function Etat104GeneratorComponent() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear - 1); // Default to previous year
  const [summary, setSummary] = useState<Etat104Summary | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  // Get years for dropdown (last 5 years)
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Fetch company ID
  const { data: companyId } = useQuery({
    queryKey: ['company-id'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      return data?.company_id;
    },
  });

  const handleGenerate = async () => {
    if (!companyId) return;

    setIsGenerating(true);
    try {
      const generatedSummary = await Etat104Generator.generate(companyId, selectedYear);
      
      // Validate
      const validation = Etat104Generator.validate(generatedSummary);
      if (!validation.valid) {
        toast({
          title: 'Erreurs de validation',
          description: validation.errors.join('\n'),
          variant: 'destructive',
        });
        return;
      }

      setSummary(generatedSummary);
      toast({
        title: 'État 104 généré !',
        description: `${generatedSummary.entries.length} clients trouvés`,
      });
    } catch (error) {
      console.error('Error generating État 104:', error);
      toast({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur de génération',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportExcel = async () => {
    if (!summary) return;

    setIsExporting(true);
    try {
      const buffer = Etat104Generator.exportToExcel(summary);
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Etat_104_${summary.year}_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Téléchargement réussi !',
        description: 'Votre État 104 Excel a été téléchargé',
      });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors de l\'export Excel',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!summary) return;

    setIsExporting(true);
    try {
      const blob = await Etat104Generator.exportToPDF(summary);
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Etat_104_${summary.year}_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Téléchargement réussi !',
        description: 'Votre État 104 PDF a été téléchargé',
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors de l\'export PDF',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6 text-primary" />
            Générateur État 104
          </CardTitle>
          <CardDescription>
            État détaillé des clients - Liste nominative et récapitulative des ventes B2B
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info Banner */}
          <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-950">
            <div className="flex gap-3">
              <Calendar className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
              <div>
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  📋 Information
                </h4>
                <p className="mt-1 text-xs text-blue-800 dark:text-blue-200">
                  L'État 104 est obligatoire pour les entreprises de production, importation et vente en gros.
                  Il doit être déposé au plus tard le <strong>30 avril</strong> de l'année N+1 avec la liasse fiscale.
                </p>
              </div>
            </div>
          </div>

          {/* Year Selection */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium">Année fiscale</label>
              <Select
                value={selectedYear.toString()}
                onValueChange={(value) => setSelectedYear(parseInt(value))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-6">
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !companyId}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <Eye className="mr-2 h-4 w-4" />
                    Générer
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {summary && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Résumé - {summary.year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-gray-600">Nombre de clients</p>
                  <p className="text-2xl font-bold">{summary.entries.length}</p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-gray-600">Total HT</p>
                  <p className="text-2xl font-bold">
                    {summary.totalHT.toLocaleString('fr-DZ', {
                      minimumFractionDigits: 2,
                    })} DA
                  </p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-gray-600">TVA Collectée</p>
                  <p className="text-2xl font-bold">
                    {summary.totalTVA.toLocaleString('fr-DZ', {
                      minimumFractionDigits: 2,
                    })} DA
                  </p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-gray-600">Total TTC</p>
                  <p className="text-2xl font-bold">
                    {summary.totalTTC.toLocaleString('fr-DZ', {
                      minimumFractionDigits: 2,
                    })} DA
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview Table */}
          <Card>
            <CardHeader>
              <CardTitle>Aperçu des données</CardTitle>
              <CardDescription>
                {summary.entries.length} client(s) avec transactions B2B
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-96 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
                    <tr className="border-b">
                      <th className="p-2 text-left font-medium">N°</th>
                      <th className="p-2 text-left font-medium">Raison Sociale</th>
                      <th className="p-2 text-left font-medium">NIF</th>
                      <th className="p-2 text-left font-medium">RC</th>
                      <th className="p-2 text-right font-medium">Total HT</th>
                      <th className="p-2 text-right font-medium">TVA</th>
                      <th className="p-2 text-right font-medium">Total TTC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.entries.map((entry, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50 dark:hover:bg-gray-900">
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2">{entry.clientName}</td>
                        <td className="p-2 font-mono text-xs">{entry.nif}</td>
                        <td className="p-2 font-mono text-xs">{entry.rc}</td>
                        <td className="p-2 text-right">
                          {entry.totalHT.toLocaleString('fr-DZ', {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td className="p-2 text-right">
                          {entry.tvaCollected.toLocaleString('fr-DZ', {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td className="p-2 text-right font-medium">
                          {entry.totalTTC.toLocaleString('fr-DZ', {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="sticky bottom-0 bg-gray-100 dark:bg-gray-800">
                    <tr className="border-t-2 font-bold">
                      <td className="p-2" colSpan={4}>TOTAL</td>
                      <td className="p-2 text-right">
                        {summary.totalHT.toLocaleString('fr-DZ', {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                      <td className="p-2 text-right">
                        {summary.totalTVA.toLocaleString('fr-DZ', {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                      <td className="p-2 text-right">
                        {summary.totalTTC.toLocaleString('fr-DZ', {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Export Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Exporter</CardTitle>
              <CardDescription>
                Téléchargez votre État 104 au format Excel ou PDF
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Button
                  onClick={handleExportExcel}
                  disabled={isExporting}
                  size="lg"
                  className="flex-1"
                >
                  {isExporting ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="mr-2 h-5 w-5" />
                  )}
                  Télécharger Excel (DGI)
                </Button>

                <Button
                  onClick={handleExportPDF}
                  disabled={isExporting}
                  size="lg"
                  variant="outline"
                  className="flex-1"
                >
                  {isExporting ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <FileText className="mr-2 h-5 w-5" />
                  )}
                  Télécharger PDF
                </Button>
              </div>

              <div className="mt-4 rounded-lg bg-yellow-50 p-4 dark:bg-yellow-950">
                <div className="flex gap-2">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                      ⚠️ Important
                    </p>
                    <p className="mt-1 text-xs text-yellow-800 dark:text-yellow-200">
                      Vérifiez les données avant soumission à la DGI. Assurez-vous que tous les NIFs sont corrects.
                      Ce document doit être accompagné de la liasse fiscale.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
